import { Space } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ProgressBar from "@/components/Elements/ProgressBar";
import UsersLinksList from "@/components/User/UsersLinksList";
import { useGetInterventionTemplate } from "@/entities/intervention-template";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import {
  FlexContainerDate,
  FlexContainerText,
  FlexContainerJsx,
} from "@/modules/flex-container";
import InterventionBreadcrumb from "@/modules/intervention/intervention-breadcrumb";
import { InterventionTemplateLabel } from "@/modules/intervention-template/intervention-template-label";
import { StateTag } from "@/modules/state-tag";
import { TranslateCost } from "@/modules/translate";
import { toDate } from "@/utilities/date";

const Info = ({ intervention }) => {
  const { t } = useTranslation();
  const { types, subTypes } = useInterventionType();

  const {
    name,
    description,
    application_date,
    scheduled_date,
    provider,
    users,
    state,
    cost,
    cost_type,
    max_days_delay,
    max_delay_date,
    project,
    progress,
    intervention_type_id,
    intervention_sub_type_id,
    intervention_template_id,
  } = intervention;

  const { template } = useGetInterventionTemplate(intervention_template_id);

  return (
    <Space orientation="vertical" size="middle">
      {name && (
        <FlexContainerText
          value={name}
          label={t("intervention:form.name.label")}
        />
      )}
      {description && (
        <FlexContainerText
          value={description}
          label={t("intervention:form.description.label")}
        />
      )}

      {max_days_delay && (
        <FlexContainerText
          label={t("intervention:form.maxDaysDelay.label")}
          value={t("global:days", {
            days: max_days_delay,
            date: toDate(max_delay_date).format(t("global:format.date")),
          })}
        />
      )}
      {application_date && (
        <FlexContainerDate
          value={application_date}
          label={t("intervention:form.creationDate.label")}
        />
      )}
      {scheduled_date && (
        <FlexContainerDate
          value={scheduled_date}
          label={t("intervention:form.scheduledDate.label")}
        />
      )}
      {provider && (
        <FlexContainerText
          value={provider}
          label={t("intervention:form.provider.label")}
        />
      )}

      {users?.length > 0 && (
        <FlexContainerJsx
          label={t("intervention:form.users.label")}
          value={<UsersLinksList users={users} />}
        />
      )}

      {cost && (
        <FlexContainerText
          label={t("intervention:form.cost.label")}
          value={<TranslateCost value={cost} />}
        />
      )}

      {cost_type && (
        <FlexContainerText
          label={t("intervention:form.costType.label")}
          value={t(`intervention:form.costType.${cost_type}`)}
        />
      )}

      {intervention_type_id && (
        <FlexContainerText
          label={t("intervention:form.type.label")}
          value={types.find(({ id }) => intervention_type_id === id)?.name}
        />
      )}

      {intervention_sub_type_id && (
        <FlexContainerText
          label={t("intervention:form.subtype.label")}
          value={
            subTypes(intervention_type_id).find(
              ({ id }) => intervention_sub_type_id === id
            )?.name
          }
        />
      )}

      {template && <InterventionTemplateLabel template={template} />}

      {state && (
        <FlexContainerJsx
          value={<StateTag value={state} type="intervention" />}
          label={t("intervention:form.state.label")}
        />
      )}

      <FlexContainerJsx
        value={<ProgressBar value={progress} />}
        label={t("intervention:form.progress.label")}
        fullWidth
      />

      <FlexContainerJsx
        value={
          <InterventionBreadcrumb
            intervention={intervention}
            exclude={["organization"]}
          />
        }
        label={t("global:hierarchy")}
      />

      {project && (
        <FlexContainerText
          value={project.name}
          link={`/projects/${project.id}`}
          label={t("global:project")}
        />
      )}
    </Space>
  );
};

Info.propTypes = {
  intervention: PropTypes.object.isRequired,
};

export default Info;
